import { useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { createElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { analytics } from '@/shared/analytics';
import { setCacheNamespace } from '@/shared/data/cacheNamespace';
import { isCookieAuthToken } from '@/shared/data/client';
import { authApi } from './api';
import { clearAuthClientState } from './authClientState';
import { AuthContext, authBypassClerkEnabled } from './authContext';
import { emitAuthDiagnostic } from './authDiagnostics';
import { createAuthBootstrapOwnerId } from './bootstrapCoordination';
import { loadCurrentClerkSessionFromState, resolveClerkSignedInSignal } from './clerkSessionState';
import { clearPostAuthRedirectAttempt } from './postAuthRedirectGuard';
import { traceAuthSessionExpired } from './refreshTraceEvents';
import {
  broadcastAuthEvent,
  clearSessionExpiredFlag,
  getClerkProvisioningFlag,
  getClerkProvisioningMode,
  getE2eAccessToken,
  getStoredSession,
  setClerkProvisioningFlag,
  setManualSignOutFlag,
  setSessionExpiredFlag,
  setSignOutTransitionFlag,
  setStoredSession,
} from './sessionStorage';
import { attemptStaleClerkOrganisationRecovery } from './staleClerkOrganisationRecovery';
import { type RefreshOutcome } from './useAuth.helpers';
import { executeAuthBootstrap } from './useAuthBootstrap';
import { useAuthBrowserBindings } from './useAuthBrowserBindings';
import { useAuthStateBypass } from './useAuthBypass';
import { useAuthClientBindings } from './useAuthClientBindings';
import { executeAuthRefresh } from './useAuthRefresh';
import type {
  AuthError,
  AuthGuardState,
  AuthSession,
  SignInPayload,
  SignUpPayload,
  UseAuthResult,
} from './types';
import type { ReactElement, ReactNode } from 'react';

type RefreshSessionOptions = {
  force?: boolean;
  syncFromStorage?: boolean;
};

const useAuthState = (): UseAuthResult => {
  const {
    isLoaded,
    isSignedIn,
    getToken,
    signOut: clerkSignOut,
    sessionId: clerkSessionId,
  } = useClerkAuth();
  const { client, setActive } = useClerk();
  const [session, setSession] = useState<AuthSession | null>(null);
  const sessionRef = useRef<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);
  const queryClient = useQueryClient();
  const refreshInFlight = useRef<Promise<RefreshOutcome> | null>(null);
  const authBootstrapOwnerIdRef = useRef(createAuthBootstrapOwnerId());
  const previousUserIdRef = useRef<string | null>(null);
  const clerkClientRef = useRef<unknown>(client as unknown);
  const clerkGetTokenRef = useRef(getToken);
  const clerkSignedInRef = useRef(isSignedIn);
  const hasSignedInSignal = resolveClerkSignedInSignal({
    isSignedIn,
    client: client as unknown,
  });
  clerkClientRef.current = client as unknown;
  clerkGetTokenRef.current = getToken;
  clerkSignedInRef.current = isSignedIn;

  const loadCurrentClerkSession = useCallback(
    async (input?: { forceTokenRefresh?: boolean }): Promise<AuthSession | null> => {
      const allowProvisioning =
        getClerkProvisioningFlag() ||
        (typeof window !== 'undefined' && window.location.pathname.startsWith('/onboarding'));
      return await loadCurrentClerkSessionFromState({
        client: clerkClientRef.current,
        getToken: clerkGetTokenRef.current,
        currentUserId: sessionRef.current?.userId ?? null,
        allowProvisioning,
        provisioningMode: getClerkProvisioningMode(),
        forceTokenRefresh: input?.forceTokenRefresh,
        bootstrapSession: authApi.bootstrapSession,
        clearProvisioningFlag: () => {
          setClerkProvisioningFlag(false);
        },
      });
    },
    [],
  );

  const clearAuthRecoveryFailures = useCallback((): void => undefined, []);

  const recoverStaleClerkOrganisation = useCallback(async (): Promise<boolean> => {
    return await attemptStaleClerkOrganisationRecovery({
      clerkSignOut,
      setActive: async (params) => {
        await setActive(params);
      },
      userId: sessionRef.current?.userId ?? null,
    });
  }, [clerkSignOut, setActive]);

  const applySession = useCallback(
    (next: AuthSession | null, options?: { persist?: boolean }): void => {
      const persist = options?.persist !== false;
      setCacheNamespace(next?.userId ?? null);
      if (!next) {
        clearPostAuthRedirectAttempt();
      } else {
        setManualSignOutFlag(false);
        setSignOutTransitionFlag(false);
      }
      sessionRef.current = next;
      if (persist) {
        if (next) {
          setStoredSession(next);
        } else {
          setStoredSession(null);
        }
      }
      setSession(next);
    },
    [],
  );

  const clearClientState = useCallback(
    (options?: { preserveSessionStorageKey?: boolean }): void => {
      clearAuthClientState(queryClient, options);
    },
    [queryClient],
  );

  const performSignOut = useCallback(
    (clearSelections: boolean): void => {
      setClerkProvisioningFlag(false);
      if (clearSelections) {
        clearClientState();
      }
      applySession(null);
      setError(null);
      setLoading(false);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('flowqa-signout', { detail: { clearSelections } }));
      }
      analytics.reset();
    },
    [applySession, clearClientState],
  );

  const signOut = useCallback(
    async (options?: { redirect?: boolean; skipClerkSignOut?: boolean }): Promise<void> => {
      const shouldRedirect = options?.redirect === true;
      const skipClerkSignOut = options?.skipClerkSignOut === true;
      setManualSignOutFlag(true);
      setSignOutTransitionFlag(true);
      clearSessionExpiredFlag();
      broadcastAuthEvent({ type: 'signout', at: Date.now() });
      performSignOut(true);
      void authApi.signOut();
      if (skipClerkSignOut) {
        return;
      }
      const signOutOptions = clerkSessionId ? { sessionId: clerkSessionId } : undefined;
      try {
        if (shouldRedirect) {
          await clerkSignOut({ ...signOutOptions, redirectUrl: '/sign-in' });
          return;
        }
        await clerkSignOut(() => undefined, signOutOptions);
      } catch {
        // Keep local sign-out complete even if Clerk rejects; sign-in recovery handles stale sessions.
      }
    },
    [clerkSessionId, clerkSignOut, performSignOut],
  );

  const expireSession = useCallback(
    (reason: 'timer' | 'refresh_error' | 'session_expired_event'): void => {
      emitAuthDiagnostic('auth_expire_session', {
        reason,
        userId: sessionRef.current?.userId ?? null,
        orgId: sessionRef.current?.orgId ?? null,
        lastOrganisationId: sessionRef.current?.lastOrganisationId ?? null,
        lastProjectId: sessionRef.current?.lastProjectId ?? null,
      });
      traceAuthSessionExpired({
        userId: sessionRef.current?.userId ?? null,
        reason,
      });
      setSessionExpiredFlag(true);
      broadcastAuthEvent({ type: 'session-expired', at: Date.now() });
      performSignOut(true);
    },
    [performSignOut],
  );

  useEffect(() => {
    const currentUserId = session?.userId ?? null;
    const previousUserId = previousUserIdRef.current;
    if (
      previousUserId &&
      currentUserId &&
      previousUserId !== currentUserId &&
      !getE2eAccessToken()
    ) {
      clearClientState({ preserveSessionStorageKey: true });
      window.dispatchEvent(
        new CustomEvent('flowqa-signout', { detail: { clearSelections: true } }),
      );
    }
    previousUserIdRef.current = currentUserId;
  }, [clearClientState, session?.userId]);

  useEffect(() => {
    const parsed = getStoredSession();
    const e2eToken = getE2eAccessToken();
    if (e2eToken) {
      const storedAccessToken = parsed?.tokens?.accessToken;
      const hasLegacyCookieToken =
        typeof storedAccessToken === 'string' && isCookieAuthToken(storedAccessToken);
      if (parsed && typeof storedAccessToken === 'string' && !hasLegacyCookieToken) {
        applySession({
          ...parsed,
          orgId: parsed.orgId ?? undefined,
          orgName: parsed.orgName ?? undefined,
          tokens: { accessToken: storedAccessToken },
        });
      } else if (hasLegacyCookieToken) {
        setStoredSession(null);
        applySession(null);
      }
      setLoading(false);
      return;
    }
    const storedAccessToken = parsed?.tokens?.accessToken;
    const hasLegacyCookieToken =
      typeof storedAccessToken === 'string' && isCookieAuthToken(storedAccessToken);
    if (hasLegacyCookieToken) {
      setStoredSession(null);
    }
    const bootstrap = async (): Promise<void> => {
      if (!isLoaded) {
        return;
      }
      const outcome = await executeAuthBootstrap({
        applySession,
        authBootstrapOwnerId: authBootstrapOwnerIdRef.current,
        clearAuthRecoveryFailures,
        clerkSignOut,
        currentSession: sessionRef.current,
        hasSignedInSignal,
        loadCurrentClerkSession: async () => await loadCurrentClerkSession(),
        recoverStaleClerkOrganisation,
        setError,
      });
      setLoading(outcome.loading);
    };
    void bootstrap();
  }, [
    applySession,
    hasSignedInSignal,
    isLoaded,
    clerkSignOut,
    loadCurrentClerkSession,
    recoverStaleClerkOrganisation,
  ]);

  const signIn = useCallback(async (input: SignInPayload): Promise<void> => {
    void input;
    await Promise.resolve();
    window.location.assign('/sign-in');
  }, []);

  const signUp = useCallback(async (input: SignUpPayload): Promise<void> => {
    void input;
    await Promise.resolve();
    window.location.assign('/sign-up');
  }, []);

  const runRefreshSession = useCallback(
    async (opts?: RefreshSessionOptions): Promise<RefreshOutcome> => {
      const forceRefresh = opts?.force === true;
      const syncFromStorage = opts?.syncFromStorage === true;
      const currentSession = sessionRef.current;
      if (getE2eAccessToken()) {
        return { session: currentSession, didRefresh: false };
      }
      if (refreshInFlight.current) {
        return await refreshInFlight.current;
      }
      const promise = (async (): Promise<RefreshOutcome> => {
        try {
          return await executeAuthRefresh({
            applySession,
            clearAuthRecoveryFailures,
            currentSession,
            expireSession,
            forceRefresh,
            loadCurrentClerkSession,
            resolveHasSignedInSignal: () =>
              resolveClerkSignedInSignal({
                isSignedIn: clerkSignedInRef.current,
                client: clerkClientRef.current,
              }),
            syncFromStorage,
            setError,
          });
        } finally {
          refreshInFlight.current = null;
        }
      })();
      refreshInFlight.current = promise;
      return await promise;
    },
    [applySession, clearAuthRecoveryFailures, expireSession, loadCurrentClerkSession],
  );

  const refreshSession = useCallback(
    async (opts?: RefreshSessionOptions): Promise<boolean> => {
      const outcome = await runRefreshSession(opts);
      return Boolean(outcome.session);
    },
    [runRefreshSession],
  );
  const runRefreshSessionRef = useRef(runRefreshSession);

  useEffect(() => {
    runRefreshSessionRef.current = runRefreshSession;
  }, [runRefreshSession]);

  useAuthClientBindings({
    clerkClientRef,
    clerkGetTokenRef,
    clearAuthRecoveryFailures,
    expireSession,
    loading,
    runRefreshSessionRef,
    sessionRef,
  });

  useEffect(() => {
    if (session?.orgId && !session.orgName) {
      const orgId = session.orgId;
      if (!orgId) return;
      void (async () => {
        try {
          const organisation = await authApi.fetchOrganisation(orgId);
          applySession({ ...session, orgName: organisation.name });
        } catch {
          // ignore fetch failures; fallback will remain
        }
      })();
    }
  }, [applySession, session]);

  const guardState = useCallback((): AuthGuardState => {
    return { session, loading, error };
  }, [session, loading, error]);

  useAuthBrowserBindings({
    clerkSignOut,
    expireSession,
    performSignOut,
    recoverStaleClerkOrganisation,
    refreshSession,
    session,
    sessionRef,
  });

  return {
    session,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    refreshSession,
    guardState,
    setSessionDirect: applySession,
  };
};

const AuthProviderWithClerk = ({ children }: { children: ReactNode }): ReactElement =>
  createElement(AuthContext.Provider, { value: useAuthState() }, children);

const AuthProviderBypass = ({ children }: { children: ReactNode }): ReactElement =>
  createElement(AuthContext.Provider, { value: useAuthStateBypass() }, children);

export const AuthProvider = ({ children }: { children: ReactNode }): ReactElement => {
  if (authBypassClerkEnabled) {
    return createElement(AuthProviderBypass, { children });
  }
  return createElement(AuthProviderWithClerk, { children });
};

export const useAuth = (): UseAuthResult => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export {
  shouldPreserveSessionOnRefreshMiss,
  shouldPreserveSessionOnSignedOutSignal,
} from './useAuth.helpers';
